import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUnifiedFilters } from "../../hooks/useUnifiedFilters";
import { useMarketplaceInfo } from "../../hooks/useMarketplaceInfo";
import { ReminderView, ModalType } from "../../types/reminderTypes";
import { getViewConfig } from "../../config/viewConfig";
import FilterButton from "./FilterButton";
import SearchInput from "./SearchInput";
import TherapyTakenToggle from "./TherapyTakenToggle";
import DateRangeFilter from "./DateRangeFilter";
import GroupsFilter, { fetchAllGroups } from "./GroupsFilter";
import StatusFilter from "./StatusFilter";
import ReactModalWrap from "../UI/Modals/ReactModalWrap/ReactModalWrap";
import moment from "moment";
import styles from "./TopFilterBar.module.scss";

interface TopFilterBarProps {
  selectedView: ReminderView;
  className?: string;
}

const TopFilterBar: React.FC<TopFilterBarProps> = ({
  selectedView,
  className = "",
}) => {
  const config = getViewConfig(selectedView);
  const searchConfig = config.search;
  const filterConfig = config.filters;
  const { filters } = useUnifiedFilters(selectedView);
  const { branchMarketplaceId, marketplaceId, isReady } = useMarketplaceInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [allGroups, setAllGroups] = useState<any[]>([]);

  // Load groups data when marketplace IDs are available
  useEffect(() => {
    if (
      isReady &&
      branchMarketplaceId &&
      marketplaceId &&
      filterConfig.groups
    ) {
      fetchAllGroups(branchMarketplaceId, marketplaceId)
        .then((res) => setAllGroups(res.data))
        .catch(console.error);
    }
  }, [isReady, branchMarketplaceId, marketplaceId, filterConfig.groups]);

  // Format date for display
  const formatDate = (dateString: string) => {
    return moment(dateString).format("DD.MM");
  };

  // Generate filter button labels and values
  const getDateLabel = () => {
    if (filters.dateRange.startDate && filters.dateRange.endDate) {
      return `${formatDate(filters.dateRange.startDate)} - ${formatDate(
        filters.dateRange.endDate
      )}`;
    }
    return "Izaberi period";
  };

  const getGroupsLabel = () => {
    if (filters.groups) {
      const count = filters.groups.split(",").length;
      return count === 1 ? "1 lista" : `${count} lista`;
    }
    return "Sve liste";
  };

  const getStatusLabel = () => {
    if (filters.statuses) {
      const count = filters.statuses.split(",").length;
      return count === 1 ? "1 status" : `${count} statusa`;
    }
    return "Svi statusi";
  };

  const closeModal = () => setActiveModal(null);

  // Clear all filters function
  const clearAllFilters = () => {
    navigate(location.pathname, { replace: true });
  };

  // Check if any filters are active
  const hasActiveFilters = !!(
    (filterConfig.dateRange &&
      (filters.dateRange.startDate || filters.dateRange.endDate)) ||
    (filterConfig.groups && filters.groups) ||
    (filterConfig.therapyTaken &&
      filters.therapyTaken &&
      filters.therapyTaken !== "all") ||
    (filterConfig.status && filters.statuses) ||
    (searchConfig.enabled && filters.search)
  );

  return (
    <div className={`${styles.topFilterBar} ${className}`}>
      {/* Search - View-specific behavior */}
      {searchConfig.enabled && (
        <SearchInput
          placeholder={searchConfig.placeholder}
          searchType={searchConfig.searchType}
          selectedView={selectedView}
          className={styles.searchInput}
        />
      )}

      {/* Date Range - Available for all views */}
      {filterConfig.dateRange && (
        <FilterButton
          label="Datum"
          value={getDateLabel()}
          hasFilter={
            !!(filters.dateRange.startDate && filters.dateRange.endDate)
          }
          isActive={activeModal === "date"}
          isVisible={filterConfig.dateRange}
          onClick={() => setActiveModal("date")}
        />
      )}

      {/* Groups - Only for Patient & Article views */}
      {filterConfig.groups && (
        <FilterButton
          label="Liste"
          value={getGroupsLabel()}
          hasFilter={!!filters.groups}
          isActive={activeModal === "groups"}
          isVisible={filterConfig.groups}
          onClick={() => setActiveModal("groups")}
        />
      )}

      {/* Status - Only for Patient & Article views */}
      {filterConfig.status && (
        <FilterButton
          label="Status"
          value={getStatusLabel()}
          hasFilter={!!filters.statuses}
          isActive={activeModal === "status"}
          isVisible={filterConfig.status}
          onClick={() => setActiveModal("status")}
        />
      )}

      {/* TherapyTaken - Only for Patient & Article views */}
      {filterConfig.therapyTaken && (
        <TherapyTakenToggle selectedView={selectedView} />
      )}

      {/* Clear All Button - Only show if filters are active */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className={styles.clearAllButton}
          title="Obriši sve filtere"
        >
          Obriši sve
        </button>
      )}

      {/* Date Range Modal */}
      {activeModal === "date" && (
        <ReactModalWrap
          isOpen={true}
          close={closeModal}
          customStyle={{ content: { width: "400px" } }}
        >
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Izaberi period</h3>
            </div>
            <DateRangeFilter
              className={styles.modalFilter}
              showHeader={false}
            />
            <div className={styles.modalFooter}>
              <button onClick={closeModal} className={styles.applyButton}>
                Primeni
              </button>
            </div>
          </div>
        </ReactModalWrap>
      )}

      {/* Groups Modal */}
      {filterConfig.groups && activeModal === "groups" && (
        <ReactModalWrap
          isOpen={true}
          close={closeModal}
          customStyle={{ content: { width: "400px", maxHeight: "500px" } }}
        >
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Izaberi liste</h3>
            </div>
            <GroupsFilter
              className={styles.modalFilter}
              showHeader={false}
              allGroups={allGroups}
            />
            <div className={styles.modalFooter}>
              <button onClick={closeModal} className={styles.applyButton}>
                Primeni
              </button>
            </div>
          </div>
        </ReactModalWrap>
      )}

      {/* Status Modal */}
      {filterConfig.status && activeModal === "status" && (
        <ReactModalWrap
          isOpen={true}
          close={closeModal}
          customStyle={{ content: { width: "400px" } }}
        >
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Izaberi statuse</h3>
            </div>
            <StatusFilter className={styles.modalFilter} showHeader={false} />
            <div className={styles.modalFooter}>
              <button onClick={closeModal} className={styles.applyButton}>
                Primeni
              </button>
            </div>
          </div>
        </ReactModalWrap>
      )}
    </div>
  );
};

export default TopFilterBar;
